import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { endpoints, type User, type Workspace, ApiError } from "./api";

interface AuthState {
  loading: boolean;
  user: User | null;
  workspaces: Workspace[];
  active: Workspace | null;
  refresh: () => Promise<void>;
  setActive: (w: Workspace) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [active, setActive] = useState<Workspace | null>(null);

  async function refresh() {
    try {
      const r = await endpoints.me();
      setUser(r.user);
      setWorkspaces(r.workspaces);
      const stored = localStorage.getItem("nox_active_ws");
      const next = r.workspaces.find((w) => w.slug === stored) ?? r.workspaces[0] ?? null;
      setActive(next);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        setWorkspaces([]);
        setActive(null);
      } else {
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function logout() {
    // Auth lives in NOX Private. Calling our local logout endpoint clears
    // the shared cookie via nox-api; once that's done the dashboard handles
    // post-logout UX. Redirect there so the user lands on the dashboard's
    // login screen rather than a now-blank smartlink page.
    try { await endpoints.logout(); } catch {}
    setUser(null);
    setWorkspaces([]);
    setActive(null);
    localStorage.removeItem("nox_active_ws");
    const noxPrivateUrl = (import.meta.env.VITE_NOX_PRIVATE_URL as string | undefined) ?? "https://noxmusic.fm";
    window.location.replace(`${noxPrivateUrl}/login`);
  }

  function selectActive(w: Workspace) {
    setActive(w);
    localStorage.setItem("nox_active_ws", w.slug);
  }

  return (
    <Ctx.Provider value={{ loading, user, workspaces, active, refresh, setActive: selectActive, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
